import * as React from "react";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  FormControl,
  Stack,
  InputAdornment,
  OutlinedInput,
  Box,
  Checkbox,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { Loader, LoadingButton } from "../../../shared";

export const StoreListDialog = (props) => {
  const ref = React.useRef(null);
  const {
    Items,
    onSearch,
    loading,
    onSelect,
    onSubmit,
    disabled = false,
    value,
    searchBar = true,
    subHeader = "",
    existedProductList = [],
    selectedItems = []
  } = props;



  const lengthOfTotal = React.useMemo(() => {
    return Items.reduce((acc, item) => acc + item.stores.length, 0);
  }, [Items]);

  const isCheckAllSelected = React.useCallback(() => {
    return lengthOfTotal > 0 && selectedItems.length === lengthOfTotal;
  }, [lengthOfTotal, selectedItems]);

  const isCheckIndeterminate = React.useCallback(() => {
    return lengthOfTotal > 0 && selectedItems.length > 0 && selectedItems.length < lengthOfTotal;
  }, [lengthOfTotal, selectedItems]);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <Stack
          justifyContent="space-between"
          height="100%"
          gap="8px"
          maxHeight={"calc(100% - 24px)"}
        >
          {subHeader && (
            <Typography
              sx={{
                pt: "27px",
              }}
            >
              {subHeader}
            </Typography>
          )}
          {searchBar && (
            <FormControl
              size="small"
              fullWidth={true}
              sx={{
                pt: "24px",
              }}
            >
              <OutlinedInput
                placeholder="Search"
                sx={{
                  height: "48px",
                  ".MuiOutlinedInput-input": {
                    pl: "0px !important",
                  },
                }}
                size="small"
                value={value}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon size="large" />
                  </InputAdornment>
                }
                endAdornment={
                  value &&
                  (
                    <InputAdornment
                      sx={{ cursor: "pointer" }}
                      onClick={() => onSearch('search', "")}
                      position="end"
                    >
                      <CloseIcon size="large" />
                    </InputAdornment>
                  )
                }
                fullWidth
                onChange={(e) => {
                  onSearch("search", e.target.value);
                }}
              />
            </FormControl>
          )}
          <Box sx={{ height: "100%", overflow: "auto" }} fullWidth>
            {Items?.length > 0 ? (
              <List sx={{ width: "100%", py: 0, bgcolor: "background.paper" }}>
                <ListItem key={value} disablePadding>
                  <ListItemButton
                    role={undefined}
                    dense
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(21, 96, 212, 0.08)",
                        borderRadius: "8px",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: "32px",
                      }}
                    >
                      <Checkbox
                        edge="start"
                        checked={isCheckAllSelected()}
                        indeterminate={isCheckIndeterminate()}
                        onChange={(e) => {
                          onSelect("all", Items, e);
                        }}
                        disableRipple
                        inputProps={{ "aria-labelledby": "all" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      id="all"
                      primary="Select All"
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: 600,
                        },
                      }}
                    />

                  </ListItemButton>
                </ListItem>
                {Items.map((value) => {
                  const labelId = `checkbox-list-label-${value}`;

                  return (
                    <>
                      {value.stores.length === 0 ? (
                        ""
                      ) : (
                        <ListOfStores
                          value={value}
                          labelId={labelId}
                          onSelect={onSelect}
                          selectedItems={selectedItems}
                          alReadyIDs={existedProductList.map((e, i) => {
                            return e.id;
                          })}
                        />
                      )}
                    </>
                  );
                })}
              </List>
            ) : (
              <Stack justifyContent="center" alignItems="center" height="100%">
                <Typography
                  sx={{ display: "inline" }}
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  fontWeight={400}
                >
                  There is no data found
                </Typography>
              </Stack>
            )}
          </Box>
          <LoadingButton
            sx={{ height: "48px" }}
            variant="contained"
            fullWidth
            onClick={onSubmit}
            disabled={disabled}
            loading={disabled}
          >
            Save
          </LoadingButton>
        </Stack>
      )}
    </>
  );
};

function ListOfStores({
  value,
  labelId,
  onSelect,
  selectedItems,
  alReadyIDs,
}) {
  const [open, setOpen] = React.useState(false);
  const isSelectedCheck = (value) => {
    let allIDs = selectedItems.concat(alReadyIDs);
    let aa = value.stores?.map((e, i) => {
      return e.id;
    });
    let a = value.stores
      ?.map((e, i) => {
        return e.id;
      })
      .every((val) => allIDs.includes(val));
    return value.stores
      ?.map((e, i) => {
        return e.id;
      })
      .every((val) => allIDs.includes(val));
  };

  const isCheckIndeterminate = (data) => {
    const subArray = data.stores.map((e, i) => e.id);
    const commonIDs1 = selectedItems.filter((val) => subArray.includes(val));
    const commonIDs2 = alReadyIDs.filter((val) => subArray.includes(val));

    let commonIDs = commonIDs1.concat(commonIDs2);
    const notAllIDs = subArray.every((val) => commonIDs.includes(val));

    return commonIDs.length > 0 && !notAllIDs;
  };

  const isSelected = (id) => {
    if (alReadyIDs.includes(id)) {
      return true;
    } else {
      return selectedItems.includes(id);
    }
  };

  const isDisabled = (value) => {
    return alReadyIDs.includes(value);
  };

  return (
    <Box
      sx={{
        backgroundColor: open ? "rgba(21, 96, 212, 0.08)" : "",
        borderRadius: "8px",
        mb: 0.5,
      }}
    >
      <ListItem key={value} disablePadding>
        <ListItemButton
          role={undefined}
          dense
          sx={{
            "&:hover": {
              backgroundColor: "rgba(21, 96, 212, 0.08)",
              borderRadius: "8px",
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: "32px",
            }}
          >
            <Checkbox
              edge="start"
              disabled={isDisabled(value)}
              checked={isSelectedCheck(value)}
              indeterminate={isCheckIndeterminate(value)}
              onChange={(e) => {
                onSelect("head", value, e);
                setOpen(true);
              }}
              disableRipple
              inputProps={{ "aria-labelledby": labelId }}
            />
          </ListItemIcon>
          <ListItemText
            id={labelId}
            primary={value.state_name}
            onClick={() => setOpen(!open)}
            sx={{
              "& .MuiTypography-root": {
                fontWeight: 600,
              },
            }}
          />
          {value.stores?.length > 0 && (
            <img
              src="/icons/ic_dropdown.svg"
              style={{
                transform: !open ? "rotate(180deg)" : "",
              }}
              onClick={() => setOpen(!open)}
            />
          )}
        </ListItemButton>
      </ListItem>
      {open &&
        value.stores.map((v) => {
          return (
            <ListItemButton
              role={undefined}
              dense
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(21, 96, 212, 0.08)",
                  borderRadius: "8px",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: "32px",
                  marginLeft: "12px",
                }}
              >
                <Checkbox
                  edge="start"
                  checked={isSelected(v.id)}
                  disabled={isDisabled(v.id)}
                  defaultChecked={true}
                  // tabIndex={-1}
                  onChange={(e) => onSelect("body", v, e)}
                  disableRipple
                />
              </ListItemIcon>
              <ListItemText  > {v.name}

              </ListItemText>
            </ListItemButton>
          );
        })}
    </Box>
  );
}
export default ListOfStores;
